/*
 * F4F Product Stock Status Widget
 *
 * Fetches per-region stock availability from the F4F API and displays
 * a color-coded badge below the buy button on product pages.
 *
 * Also swaps the server-rendered payment plans (metafield-based, global
 * status) for the region-resolved set served by
 * GET /api/v2/products/{sku}/payment-options, so the badge and the plans
 * can never disagree. The theme builds no plans of its own: no price
 * arithmetic, no month formatting, no title construction. The Liquid
 * block stays as the initial paint and as the fallback when the API
 * fails, because a customer with no plans cannot buy.
 *
 * Depends on: f4f-api-client.js (window.F4FApi.getProductStock,
 * getWarehouses, getProductPaymentOptions)
 *
 * Country detection reads the Shopify localization form hidden input,
 * then maps the ISO country code to the serving warehouse region via
 * the GET /api/v1/warehouses endpoint (cached per session).
 */

(function () {
  'use strict';

  // ── Warehouse mapping (fetched from API, cached) ───────
  // Built from GET /api/v1/warehouses response: { warehouses: { US: ["US","AG",...], ... } }
  // Inverted to country→warehouse lookup. CN is catch-all for unmapped countries.
  var countryToWarehouse = null; // Populated on first use
  var warehousePromise = null;   // Singleton promise to avoid duplicate fetches
  var DEFAULT_WAREHOUSE = 'CN';

  function loadWarehouseMapping() {
    if (warehousePromise) return warehousePromise;

    if (!window.F4FApi || !window.F4FApi.getWarehouses) {
      return Promise.resolve(null);
    }

    warehousePromise = window.F4FApi.getWarehouses()
      .then(function (data) {
        if (!data || !data.warehouses) return null;
        // Invert the mapping: { US: ["US","AG"] } → { US: "US", AG: "US" }
        countryToWarehouse = {};
        Object.keys(data.warehouses).forEach(function (warehouseCode) {
          var countries = data.warehouses[warehouseCode];
          countries.forEach(function (countryCode) {
            countryToWarehouse[countryCode] = warehouseCode;
          });
        });
        return countryToWarehouse;
      })
      .catch(function (err) {
        console.warn('[F4F Stock] Failed to load warehouse mapping:', err);
        return null;
      });

    return warehousePromise;
  }

  // ── Status display config ──────────────────────────────
  var STATUS_LABELS = {
    in_stock: 'In Stock',
    low_stock: 'Limited Stock',
    out_of_stock: 'Out of Stock',
    pre_order: 'Pre-Order',
    waitlist: 'Waitlist'
  };

  var WAREHOUSE_LABELS = {
    US: 'United States',
    UK: 'United Kingdom',
    CA: 'Canada',
    AU: 'Australia',
    SK: 'Europe',
    CN: 'Asia'
  };

  // ── DOM helpers ────────────────────────────────────────

  function getCustomerCountry() {
    var input = document.querySelector('input[name="country_code"]');
    if (input && input.value) return input.value.toUpperCase();
    if (window.mdApp_currentCountryCode) return window.mdApp_currentCountryCode.toUpperCase();
    return '';
  }

  function getWarehouseRegion(countryCode) {
    if (!countryToWarehouse) return DEFAULT_WAREHOUSE;
    return countryToWarehouse[countryCode] || DEFAULT_WAREHOUSE;
  }

  function getCurrentSku() {
    // Try the payment options data attribute first (always has first variant SKU).
    // Scoped to the plan list because cart-form.liquid renders the same
    // attribute into the cart drawer on EVERY page - an unscoped lookup made
    // the badge show the CART item's stock on pages without a plan block.
    var paymentEl = document.querySelector('.product-page-payment-options[data-product-payment-selection-sku]');
    if (paymentEl) return paymentEl.getAttribute('data-product-payment-selection-sku');
    // Fallback to the SKU display element
    var skuEl = document.querySelector('[data-js-product-sku]');
    if (skuEl) return skuEl.textContent.replace(/^SKU:\s*/i, '').trim();
    return '';
  }

  // ── Widget rendering ───────────────────────────────────

  var containerEl = null;
  var currentSku = '';
  var stockCache = {};

  function createContainer() {
    containerEl = document.createElement('div');
    containerEl.className = 'f4f-stock';

    // Insert after the product title (h1.product-title), in normal document flow.
    // The .product-badges-holder is position:absolute so we can't insert near it.
    var productTitle = document.querySelector('product-header .product-title');
    if (productTitle) {
      productTitle.parentNode.insertBefore(containerEl, productTitle.nextSibling);
      return;
    }
    // Fallback: insert before the price area
    var priceReviews = document.querySelector('.price-reviews');
    if (priceReviews) {
      priceReviews.parentNode.insertBefore(containerEl, priceReviews);
      return;
    }
    // Last resort: insert after the add-to-cart form
    var productForm = document.querySelector('product-form');
    if (productForm) {
      productForm.parentNode.insertBefore(containerEl, productForm.nextSibling);
    }
  }

  function hideExistingBadge() {
    // Hide the theme's static stock status badge (e.g. "PRE-ORDER", "In Stock")
    // since our dynamic API-driven badge replaces it with per-region data
    var statusBadges = document.querySelectorAll('.product-badge__status');
    statusBadges.forEach(function (badge) {
      badge.style.display = 'none';
    });
  }

  function renderLoading() {
    if (!containerEl) return;
    containerEl.innerHTML =
      '<span class="f4f-stock__badge f4f-stock__badge--loading">' +
        '<span class="f4f-stock__dot"></span>' +
        'Loading...' +
      '</span>';
    containerEl.classList.remove('f4f-stock--hidden');
  }

  function renderBadge(status, warehouseCode) {
    if (!containerEl) return;
    var label = STATUS_LABELS[status] || status;
    var regionLabel = WAREHOUSE_LABELS[warehouseCode] || warehouseCode;

    containerEl.innerHTML =
      '<span class="f4f-stock__badge f4f-stock__badge--' + status + '">' +
        '<span class="f4f-stock__dot"></span>' +
        label +
        '<span class="f4f-stock__region">(' + regionLabel + ')</span>' +
      '</span>';
    containerEl.classList.remove('f4f-stock--hidden');
    containerEl.classList.add('f4f-stock--loaded');
    hideExistingBadge();
  }

  function hide() {
    if (containerEl) containerEl.classList.add('f4f-stock--hidden');
  }

  // ── Fetch and display ──────────────────────────────────

  function fetchAndRender(sku) {
    if (!sku) { hide(); return; }
    if (sku === currentSku) return;
    currentSku = sku;

    // Payment plans ride along with every stock refresh (variant changes)
    fetchAndRenderPaymentOptions(sku);

    // Check cache first
    if (stockCache[sku]) {
      displayStock(stockCache[sku]);
      return;
    }

    renderLoading();

    if (!window.F4FApi || !window.F4FApi.getProductStock) {
      console.warn('[F4F Stock] F4FApi.getProductStock not available');
      hide();
      return;
    }

    window.F4FApi.getProductStock(sku)
      .then(function (data) {
        if (!data) { hide(); return; }
        stockCache[sku] = data;
        // Only render if this is still the current SKU (prevents race conditions)
        if (currentSku === sku) displayStock(data);
      })
      .catch(function (err) {
        console.warn('[F4F Stock] Failed to fetch stock for ' + sku + ':', err);
        hide();
      });
  }

  function displayStock(data) {
    var overallStatus = data.status;
    var country = getCustomerCountry();
    var warehouse = getWarehouseRegion(country);

    // For non-in_stock statuses, the status is uniform across all regions
    if (overallStatus !== 'in_stock') {
      renderBadge(overallStatus, warehouse);
      return;
    }

    // For in_stock products, show the customer's specific region availability
    var regionStatus = (data.regions && data.regions[warehouse]) || 'out_of_stock';
    renderBadge(regionStatus, warehouse);
  }

  // ── Payment options (region-aware, API-served) ─────────
  // Replaces the Liquid-rendered plan list (main-product.liquid) with the
  // set returned by GET /api/v2/products/{sku}/payment-options. The API
  // resolves country to warehouse region server-side, so the raw ISO
  // country code is passed through unmapped.

  var paymentCache = {};      // (sku|country) -> options array
  var currentPaymentKey = ''; // Guards against out-of-order responses

  function getPaymentList() {
    return document.querySelector('.product-page-payment-options');
  }

  // The plan titles are a live contract (radio value -> payment_selection
  // cookie -> cart display -> F4FPay delivery_status). A malformed option
  // must never render, so the whole response falls back to the Liquid set
  // unless every option is well-formed.
  function validPaymentOptions(data) {
    if (!data || !Array.isArray(data.options)) return null;
    for (var i = 0; i < data.options.length; i++) {
      var option = data.options[i];
      if (!option || typeof option.title !== 'string' || option.title === '' ||
          typeof option.pay_today !== 'number' || typeof option.remaining_balance !== 'number') {
        return null;
      }
    }
    return data.options;
  }

  function strongEl(text) {
    var el = document.createElement('strong');
    el.textContent = text;
    return el;
  }

  function priceSpan(amount) {
    var span = document.createElement('span');
    span.className = 'price money product-page-payment-options-price';
    // Raw NET number after "$", exactly as the Liquid template emits it.
    // price-tax-adjustments.js (VAT post-processor) rewrites the text and marks the
    // element data-tax-processed for countries that need tax applied.
    span.textContent = '$' + amount;
    return span;
  }

  // Mirrors the markup of main-product.liquid's Liquid loop exactly:
  // same classes, same ids, same radio name/value, and "Number of
  // Instalments" printed only when the count is greater than 1.
  function buildPaymentOption(option, index) {
    var li = document.createElement('li');
    li.className = 'product-page-payment-options-item';

    var input = document.createElement('input');
    input.type = 'radio';
    input.id = 'payment-option-' + (index + 1);
    input.name = 'payment_option';
    input.value = option.title;
    input.required = true;

    var label = document.createElement('label');
    label.htmlFor = input.id;

    var title = document.createElement('div');
    title.className = 'payment-option-title';
    title.textContent = option.title;

    var shortDescription = document.createElement('div');
    shortDescription.className = 'payment-option-short-description';
    shortDescription.textContent = option.short_description || '';

    var details = document.createElement('div');
    details.className = 'payment-option-details';
    details.appendChild(strongEl('Pay Today:'));
    details.appendChild(document.createTextNode(' '));
    details.appendChild(priceSpan(option.pay_today));
    details.appendChild(document.createElement('br'));
    details.appendChild(strongEl('Remaining Balance:'));
    details.appendChild(document.createTextNode(' '));
    details.appendChild(priceSpan(option.remaining_balance));
    details.appendChild(document.createElement('br'));
    if (option.number_instalments > 1) {
      details.appendChild(strongEl('Number of Instalments:'));
      details.appendChild(document.createTextNode(' ' + option.number_instalments));
    }

    label.appendChild(title);
    label.appendChild(shortDescription);
    label.appendChild(details);
    li.appendChild(input);
    li.appendChild(label);
    return li;
  }

  // A saved cookie can name a plan that is no longer offered (the ORDER
  // Flexipay title contains a month that moves). Re-apply a still-valid
  // selection to the freshly rendered radios; clear a stale one and leave
  // nothing selected so the customer must choose. Never silently
  // substitute a different plan.
  function reconcilePaymentCookie(sku) {
    // getCookie/setCookie are theme.js globals (loaded before this file)
    if (typeof getCookie !== 'function' || typeof setCookie !== 'function') return;

    var cookieName = 'payment_selection_' + sku;
    var saved = getCookie(cookieName);
    if (!saved) return;

    var list = getPaymentList();
    if (!list) return;

    var matched = false;
    list.querySelectorAll('input[type="radio"][name="payment_option"]').forEach(function (radio) {
      if (radio.value === saved) {
        radio.checked = true;
        radio.closest('.product-page-payment-options-item').classList.add('selected-option');
        matched = true;
      }
    });

    if (!matched) {
      console.log('[F4F Stock] Clearing stale payment selection for ' + sku + ': "' + saved + '" is no longer offered');
      setCookie(cookieName, '', -1);
    }
  }

  function renderPaymentOptions(sku, options) {
    var oldList = getPaymentList();
    // No Liquid-rendered list means the block is gated off (no metafield,
    // or show_flexipayment disabled) - leave the page alone.
    if (!oldList) return;

    var wrapper = oldList.closest('.product-page-payment-options-wrapper');

    var newList = document.createElement('ul');
    newList.className = 'product-page-payment-options';
    // getCurrentSku() reads this attribute first - it must survive the swap
    newList.setAttribute('data-product-payment-selection-sku', sku);
    options.forEach(function (option, index) {
      newList.appendChild(buildPaymentOption(option, index));
    });

    // Swap the whole list as ONE node. price-tax-adjustments.js's VAT MutationObserver
    // (watching .product-page-payment-options-wrapper) only reprocesses
    // prices that are DESCENDANTS of an added node, so the wrapper must
    // stay in place and the list must not be appended li by li.
    oldList.replaceWith(newList);

    // An empty option set (soldout and the other no-plan statuses) hides
    // the plan block entirely
    if (wrapper) wrapper.style.display = options.length === 0 ? 'none' : '';

    reconcilePaymentCookie(sku);
  }

  function fetchAndRenderPaymentOptions(sku) {
    if (!sku) return;
    if (!getPaymentList()) return;
    if (!window.F4FApi || !window.F4FApi.getProductPaymentOptions) return;

    var country = getCustomerCountry();
    var cacheKey = sku + '|' + country;
    currentPaymentKey = cacheKey;

    if (paymentCache[cacheKey]) {
      renderPaymentOptions(sku, paymentCache[cacheKey]);
      return;
    }

    window.F4FApi.getProductPaymentOptions(sku, country)
      .then(function (data) {
        var options = validPaymentOptions(data);
        if (!options) {
          // 404 (endpoint not live / unknown SKU) or malformed payload:
          // keep the Liquid-rendered fallback, but still reconcile the
          // cookie against the titles actually on screen. Skip if a newer
          // request has superseded this one - its render owns the cookie.
          console.log('[F4F Stock] No API payment options for ' + sku + ', keeping Liquid-rendered plans');
          if (currentPaymentKey === cacheKey) reconcilePaymentCookie(sku);
          return;
        }
        paymentCache[cacheKey] = options;
        // Only render if this is still the current request (prevents races)
        if (currentPaymentKey === cacheKey) {
          console.log('[F4F Stock] Rendering ' + options.length + ' API payment option(s) for ' + sku + ' (' + (data.region || '?') + ' ' + (data.status || '?') + ')');
          renderPaymentOptions(sku, options);
        }
      })
      .catch(function (err) {
        // Fail soft: the Liquid-rendered plans stay in place. Same
        // superseded-request guard as the null branch above.
        console.warn('[F4F Stock] Failed to fetch payment options for ' + sku + ':', err);
        if (currentPaymentKey === cacheKey) reconcilePaymentCookie(sku);
      });
  }

  // ── Variant change listener ────────────────────────────
  // The theme dispatches a custom 'variant-change' event on <product-page>
  // and also updates [data-js-product-sku] on variant selection.

  function onVariantChange() {
    // Small delay to let the theme update the SKU display element
    setTimeout(function () {
      var sku = getCurrentSku();
      if (sku && sku !== currentSku) {
        currentSku = ''; // Reset to force re-fetch
        fetchAndRender(sku);
      }
    }, 50);
  }

  // ── Init ───────────────────────────────────────────────

  function init() {
    // Skip coming-soon products: the storefront intentionally hides
    // pricing and stock/status info until pre-orders open, so the API
    // badge (e.g. "Pre-Order (United States)") should stay hidden too.
    // main-product.liquid sets the .product-page-coming-soon class when
    // product.metafields.f4f.available_from is in the future.
    var productPage = document.querySelector('product-page');
    if (productPage && productPage.classList.contains('product-page-coming-soon')) {
      console.log('[F4F Stock] Coming-soon product, skipping stock badge.');
      return;
    }

    console.log('[F4F Stock] Initializing...');
    createContainer();
    if (!containerEl) {
      console.warn('[F4F Stock] Could not create container — no insertion point found');
      return;
    }
    console.log('[F4F Stock] Container created:', containerEl.parentElement?.tagName, containerEl.parentElement?.className);

    // Load warehouse mapping first, then fetch stock for the current SKU.
    // Both requests fire in parallel for speed.
    var sku = getCurrentSku();
    console.log('[F4F Stock] SKU:', sku);
    console.log('[F4F Stock] F4FApi available:', !!window.F4FApi, 'getProductStock:', !!window.F4FApi?.getProductStock);
    var mappingReady = loadWarehouseMapping();
    var stockReady = null;

    // Fetch the region-aware payment plans alongside the stock call. The
    // API resolves the country itself, so this does not wait on the
    // warehouse mapping.
    fetchAndRenderPaymentOptions(sku);

    if (sku && window.F4FApi && window.F4FApi.getProductStock) {
      renderLoading();
      currentSku = sku;
      stockReady = window.F4FApi.getProductStock(sku)
        .then(function (data) {
          console.log('[F4F Stock] API response for ' + sku + ':', data);
          if (data) stockCache[sku] = data;
          return data;
        })
        .catch(function (err) {
          console.warn('[F4F Stock] Failed to fetch stock for ' + sku + ':', err);
          return null;
        });
    }

    // Once both are done, render
    Promise.all([mappingReady, stockReady || Promise.resolve(null)])
      .then(function (results) {
        var data = results[1];
        if (!data || currentSku !== sku) { hide(); return; }
        displayStock(data);
      });

    // Listen for variant changes
    var productPage = document.querySelector('product-page');
    if (productPage) {
      productPage.addEventListener('variant-change', onVariantChange);
    }

    // Also observe SKU text changes as a fallback
    var skuEl = document.querySelector('[data-js-product-sku]');
    if (skuEl && window.MutationObserver) {
      var observer = new MutationObserver(onVariantChange);
      observer.observe(skuEl, { childList: true, characterData: true, subtree: true });
    }
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
